/**
 * Dashboard-side notification helpers for the inbox.
 *
 * Surfaces three layers of attention so an agent never misses a new
 * conversation while looking at another tab / window:
 *
 *  1. A short Web-Audio tone with two motifs — one for "new chat needs
 *     attention" (handoff arriving / claim assigned to me) and a softer
 *     one for "visitor just sent a message in an existing chat".
 *  2. A `(N) Inbox · Bruno` prefix on the document title so the unread
 *     count is visible in the OS-level tab bar.
 *  3. The browser Notification API (when the user has granted
 *     permission) for an OS-level toast that fires even if the dashboard
 *     window is fully hidden.
 *
 * The helper keeps a single instance worth of state; it's intended to be
 * imported as a singleton (`agentNotifier`) so the unread counter is
 * shared across the app.
 */

export type AgentNotificationKind = 'handoff' | 'message';

export interface AgentNotifyOptions {
  kind: AgentNotificationKind;
  title: string;
  body?: string;
  /**
   * Conversation id the notification belongs to, used as the OS
   * notification tag so a rapid-fire chat doesn't spawn a stack of
   * pop-ups (each new ping replaces the previous one for that chat).
   */
  tag?: string;
  /**
   * When clicked, the OS notification calls this callback. Typically
   * focuses the window and selects the conversation in the inbox.
   */
  onClick?: () => void;
}

class AgentNotifier {
  private unread = 0;
  private originalTitle: string | null = null;
  private audioCtx: AudioContext | null = null;
  private permissionRequested = false;

  /**
   * Ask the browser for permission to show OS notifications. Safe to
   * call repeatedly — the underlying API itself caches the prompt
   * decision, but we also short-circuit so we never re-prompt during
   * the same session.
   */
  requestPermission(): void {
    if (this.permissionRequested) return;
    this.permissionRequested = true;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      try {
        void Notification.requestPermission().catch(() => {});
      } catch {
        /* some embedded browsers throw — ignore */
      }
    }
  }

  notify(opts: AgentNotifyOptions): void {
    this.unread += 1;
    this.paintTitle();
    this.playPing(opts.kind);
    this.showOsNotification(opts);
  }

  /**
   * Decrement the unread counter. Called after the agent opens or
   * focuses a specific conversation.
   */
  acknowledge(amount = 1): void {
    if (this.unread === 0) return;
    this.unread = Math.max(0, this.unread - amount);
    if (this.unread === 0) this.restoreTitle();
    else this.paintTitle();
  }

  clear(): void {
    if (this.unread === 0 && this.originalTitle === null) return;
    this.unread = 0;
    this.restoreTitle();
  }

  // ---------- internals ----------

  private paintTitle(): void {
    if (typeof document === 'undefined') return;
    if (this.originalTitle === null) this.originalTitle = document.title || '';
    document.title = `(${this.unread}) ${this.originalTitle}`.trim();
  }

  private restoreTitle(): void {
    if (typeof document === 'undefined') return;
    if (this.originalTitle === null) return;
    document.title = this.originalTitle;
    this.originalTitle = null;
  }

  /**
   * Generate a short attention-grabbing tone via Web Audio. Handoff
   * pings use a higher, two-note motif so an agent learns to react more
   * urgently to "new chat assigned" than to "visitor said something".
   */
  private playPing(kind: AgentNotificationKind): void {
    try {
      const Ctor =
        (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      if (!this.audioCtx) this.audioCtx = new Ctor();
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});

      const motif =
        kind === 'handoff'
          ? [880, 1175, 1397] // higher, three-note urgent ping
          : [659, 880]; // softer two-note message ping
      const now = ctx.currentTime;
      motif.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const start = now + i * 0.13;
        const stop = start + 0.12;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(kind === 'handoff' ? 0.18 : 0.12, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, stop);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(stop + 0.02);
      });
    } catch {
      /* Web Audio errors must never break the dashboard. */
    }
  }

  private showOsNotification(opts: AgentNotifyOptions): void {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    // Don't open a system notification for events that happen while the
    // agent is staring at the dashboard — the in-app sound + title are
    // sufficient and an OS toast would be noisy.
    if (typeof document !== 'undefined' && !document.hidden) return;
    try {
      const n = new Notification(opts.title, {
        body: opts.body,
        tag: opts.tag,
        // Re-fire the OS sound for handoff pings even if the tag is
        // re-used (silent: false) so an agent doesn't miss a second
        // assignment to the same conversation.
        silent: opts.kind !== 'handoff',
        icon: '/favicon.ico',
      });
      n.onclick = () => {
        try {
          window.focus();
        } catch {
          /* ignore */
        }
        opts.onClick?.();
        n.close();
      };
    } catch {
      /* OS notification creation can throw on some platforms; ignore. */
    }
  }
}

/** Process-wide singleton so the unread counter is shared across the app. */
export const agentNotifier = new AgentNotifier();
